/**
 * Prose clamped to a line count, with a toggle to see the rest — except when the rest is
 * barely anything. Shared by the Notes card (a note's body) and a task row (its notes),
 * since both are "some free text, maybe long, previewed to a settings-controlled depth."
 *
 * The expand toggle used to be a "Read more…"/"Show less" text link. It's `EyeToggle` now
 * (the same open/closed eye every other expand control in the app already uses —
 * `GoneQuiet`, `StructureRow`, `QuickAddCard`), paired with a copy button.
 *
 * Both float at the top-right of the text — the same trick a reference D&D app used for an
 * illustration inside a paragraph (`float: right` on an `<img>`, with `shape-outside` for a
 * non-rectangular one), just with a small button cluster instead of an image and no
 * `shape-outside` needed, since a rectangle already wraps for free. Genuine text reflow, not
 * an overlay: three earlier attempts tried to make the controls sit at the *bottom* corner
 * instead (a padding reservation, then a mask-faded absolute overlay), and both amounted to
 * the same trick in different clothes, because there is no such thing as "float from the
 * bottom" in CSS — a float only affects lines from where it's inserted *downward*, so
 * reaching the trailing edge of a variable-length clamp would need the exact character
 * offset where the last line starts, measured and split by hand. Top-right sidesteps the
 * problem entirely: inserted as the very first child, the float only ever affects however
 * many of the *first* lines its own height spans, which needs no measurement at all — the
 * same reason the reference image works the way it does.
 *
 * One consequence worth knowing rather than a bug: the float narrows the first line or two,
 * so a preview clamped to N lines fits slightly less text before offering to expand than it
 * would unobstructed — inherent to reflowing around anything at the top of a block, the
 * reference image does the same to whatever paragraph it sits in.
 *
 * Collapsed, `.clamp-wrap`'s own `::after` fades the last line or so toward the card's
 * background — a decorative "there's more below," not `mask-image` on the clamped text
 * itself: a mask acts on everything painted as part of that element, floated controls
 * included, and on a short clamp the fade region reaches high enough to dim the buttons too.
 * A background gradient on its own layer can't touch pixels it isn't painted over.
 */

import { useLayoutEffect, useRef, useState } from "react";
import { Check, Copy } from "./icons";
import { EyeToggle } from "./EyeToggle";

/**
 * Text hidden beyond the clamp is worth a "Read more" only if there's a meaningful amount
 * of it. A single line just past the cutoff might be a whole sentence, or it might be one
 * short word carried over by a line-break — indistinguishable by height alone, since a
 * wrapped line box is always a full line tall whether it holds one word or ten.
 *
 * `Range.getClientRects()` sees the true laid-out lines regardless of `overflow: hidden` or
 * `-webkit-line-clamp`, so the width of the *last* rendered line — full line count, not just
 * the clamp — is how much of that final line is actually text versus empty space. Two or
 * more hidden lines are always worth it; exactly one is only worth it if that line is at
 * least half full.
 */
function shouldOfferExpand(el: HTMLElement, clampLines: number): boolean {
  const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
  const step = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 20;
  const totalLines = Math.round(el.scrollHeight / step);

  if (totalLines <= clampLines) return false;
  if (totalLines > clampLines + 1) return true;

  const range = document.createRange();
  range.selectNodeContents(el);
  const rects = range.getClientRects();
  if (rects.length === 0 || el.clientWidth === 0) return true;

  const lastLine = rects[rects.length - 1];
  const MIN_LAST_LINE_FILL = 0.5;
  return lastLine.width / el.clientWidth >= MIN_LAST_LINE_FILL;
}

/**
 * `maxLines <= 0` renders nothing at all — the settings page's "0" means "don't show this
 * preview," not "show an empty clamped box."
 */
export function ClampedText({
  text,
  maxLines,
  textClassName,
}: {
  text: string;
  maxLines: number;
  textClassName: string;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [copied, setCopied] = useState(false);

  useLayoutEffect(() => {
    // Once it's known to be worth expanding, stay decided — measuring again while expanded
    // would compare the full height to itself and (wrongly) conclude there is nothing left
    // to reveal, hiding the "Show less" control that got it here.
    if (expanded) return;

    const el = ref.current;
    if (!el) return;
    setCanExpand(shouldOfferExpand(el, maxLines));
  }, [text, expanded, maxLines]);

  if (maxLines <= 0) return null;

  // Clamp only while there both is more to show *and* it hasn't been shown yet. If nothing
  // ever set `canExpand` — the text fits already, or the overflow was too trivial to
  // bother — this renders the full text unclamped from the first paint, with no button and
  // no way to lose the tail end permanently.
  const clamped = canExpand && !expanded;

  /**
   * Copy the note, and say so — including when the browser will not let us.
   *
   * Same shape as `TokensCard`'s own `copy`: `navigator.clipboard` is undefined outside a
   * secure context, and this app is served over plain HTTP on the LAN, so the happy path
   * has to have a real fallback rather than an optional-chained no-op. Selecting the text
   * (the same paragraph node the clamp measurement already holds a ref to) means one
   * keystroke instead of none.
   */
  async function copy() {
    try {
      if (window.isSecureContext && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
        return;
      }
    } catch {
      // Permission denied, or no clipboard. Fall through to selecting it.
    }

    const node = ref.current;
    if (node) {
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }

  return (
    <div className={`clamp-wrap${clamped ? " fade" : ""}`}>
      <p
        ref={ref}
        className={`${textClassName}${clamped ? " clamped" : ""}`}
        style={clamped ? { WebkitLineClamp: maxLines } : undefined}
      >
        <span className="clamp-controls">
          {canExpand && (
            <EyeToggle open={expanded} onToggle={() => setExpanded((v) => !v)} label="the rest of this note" />
          )}
          <button
            type="button"
            className="btn ghost small"
            onClick={() => void copy()}
            aria-label={copied ? "Copied" : "Copy note"}
            title={copied ? "Copied" : "Copy note"}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </span>
        {text}
      </p>
    </div>
  );
}
